/**
 * The attorney's edits of the Detailed Description framework, as whole frameworks to send.
 *
 * Every write replaces the framework whole, so each edit here takes the sections as the
 * page holds them and returns the sections to send. Who wrote each item and what Removed
 * holds are the server's to derive, so nothing here carries an origin.
 */

import type {
  DefinitionLineEdit,
  RungEdit,
  DetailedDescriptionFramework,
  RemovedSection,
  RemovedSubsection,
  SectionEdit,
} from "@patent-studio/api-types";

type Line = NonNullable<SectionEdit["subsections"]>[number];
type Served = NonNullable<DetailedDescriptionFramework["sections"]>[number];
type Stored = NonNullable<Served["subsections"]>[number];

/** The lines a section holds; a section built with none carries no list. */
function linesOf(section: SectionEdit): Line[] {
  return section.subsections ?? [];
}

/** A stored subsection as an edit sends it: the same id and words, no origin. */
function asLine(subsection: Stored): Line {
  switch (subsection.kind) {
    case "text":
      return {
        kind: "text",
        subsection_id: subsection.subsection_id,
        text: subsection.text,
      };
    case "figure":
      return {
        kind: "figure",
        subsection_id: subsection.subsection_id,
        file_id: subsection.file_id,
      };
    case "claim": {
      const line: Line = {
        kind: "claim",
        subsection_id: subsection.subsection_id,
        claim_id: subsection.claim_id,
        text: subsection.text,
      };
      // The rungs ride on the row, so an edit anywhere sends them back as they were.
      const ladders = subsection.ranges ?? [];
      return ladders.length === 0
        ? line
        : {
            ...line,
            ranges: ladders.map((ladder) => ({
              claim_id: ladder.claimed.claim_id,
              recital_id: ladder.claimed.recital_id,
              rungs: (ladder.rungs ?? []).map((rung) => ({
                item_id: rung.item_id,
                key: rung.key,
                accepted: rung.accepted,
              })),
            })),
          };
    }
    case "examples":
      return { kind: "examples", subsection_id: subsection.subsection_id };
    case "definitions":
      return {
        kind: "definitions",
        subsection_id: subsection.subsection_id,
        lines: (subsection.lines ?? []).map(({ item_id, key, text }) => ({
          item_id,
          key,
          text,
        })),
      };
  }
}

function asSection(section: Served): SectionEdit {
  return {
    section_id: section.section_id,
    title: section.title,
    subsections: (section.subsections ?? []).map(asLine),
  };
}

export function toEdits(
  framework: DetailedDescriptionFramework,
): SectionEdit[] {
  return (framework.sections ?? []).map(asSection);
}

export function renameSection(
  edits: SectionEdit[],
  sectionId: string,
  title: string,
): SectionEdit[] {
  return edits.map((section) =>
    section.section_id === sectionId ? { ...section, title } : section,
  );
}

/** A text line's or a claim's words; a figure has none of its own. */
export function rewordLine(
  edits: SectionEdit[],
  subsectionId: string,
  text: string,
): SectionEdit[] {
  return edits.map((section) => ({
    ...section,
    subsections: linesOf(section).map((line) =>
      line.subsection_id === subsectionId && line.kind !== "figure"
        ? { ...line, text }
        : line,
    ),
  }));
}

/** A definition as the attorney types it: the term and what it means here. */
export interface DefinitionWords {
  key: string;
  text: string;
}

/** The sections with one Definitions subsection's lines changed, everything else as it is. */
function withDefinitionLines(
  edits: SectionEdit[],
  subsectionId: string,
  change: (lines: DefinitionLineEdit[]) => DefinitionLineEdit[],
): SectionEdit[] {
  return edits.map((section) => ({
    ...section,
    subsections: linesOf(section).map((line) =>
      line.kind === "definitions" && line.subsection_id === subsectionId
        ? { ...line, lines: change(line.lines ?? []) }
        : line,
    ),
  }));
}

/** A definition the attorney writes, after the others; it is theirs and needs no source. */
export function addDefinition(
  edits: SectionEdit[],
  subsectionId: string,
  words: DefinitionWords,
  itemId: string,
): SectionEdit[] {
  return withDefinitionLines(edits, subsectionId, (lines) => [
    ...lines,
    { item_id: itemId, ...words },
  ]);
}

/** A definition in the attorney's words, kept as they wrote it. */
export function rewordDefinition(
  edits: SectionEdit[],
  subsectionId: string,
  itemId: string,
  words: DefinitionWords,
): SectionEdit[] {
  return withDefinitionLines(edits, subsectionId, (lines) =>
    lines.map((line) =>
      line.item_id === itemId ? { item_id: itemId, ...words } : line,
    ),
  );
}

/** The Definitions without one line; a dropped Praxis term is not suggested again. */
export function dropDefinition(
  edits: SectionEdit[],
  subsectionId: string,
  itemId: string,
): SectionEdit[] {
  return withDefinitionLines(edits, subsectionId, (lines) =>
    lines.filter((line) => line.item_id !== itemId),
  );
}

/** Which claimed range a ladder is: the claim that states it, and the thing it recites. */
export interface LadderKey {
  claimId: string;
  recitalId: string;
}

/** The sections with one ladder's rungs changed on one claim row, everything else as it is. */
function withRungs(
  edits: SectionEdit[],
  subsectionId: string,
  ladder: LadderKey,
  change: (rungs: RungEdit[]) => RungEdit[],
): SectionEdit[] {
  return edits.map((section) => ({
    ...section,
    subsections: linesOf(section).map((line) =>
      line.kind === "claim" && line.subsection_id === subsectionId
        ? {
            ...line,
            ranges: (line.ranges ?? []).map((held) =>
              held.claim_id === ladder.claimId &&
              held.recital_id === ladder.recitalId
                ? { ...held, rungs: change(held.rungs ?? []) }
                : held,
            ),
          }
        : line,
    ),
  }));
}

/** A rung accepted or set back to a suggestion; only an accepted rung is stated. */
export function acceptRung(
  edits: SectionEdit[],
  subsectionId: string,
  ladder: LadderKey,
  itemId: string,
  accepted: boolean,
): SectionEdit[] {
  return withRungs(edits, subsectionId, ladder, (rungs) =>
    rungs.map((rung) =>
      rung.item_id === itemId ? { ...rung, accepted } : rung,
    ),
  );
}

/** A rung in the attorney's words, theirs and accepted as written. */
export function rewordRung(
  edits: SectionEdit[],
  subsectionId: string,
  ladder: LadderKey,
  itemId: string,
  key: string,
): SectionEdit[] {
  return withRungs(edits, subsectionId, ladder, (rungs) =>
    rungs.map((rung) =>
      rung.item_id === itemId ? { item_id: itemId, key, accepted: true } : rung,
    ),
  );
}

/** A rung the attorney adds after the others, accepted as written. */
export function addRung(
  edits: SectionEdit[],
  subsectionId: string,
  ladder: LadderKey,
  key: string,
  itemId: string,
): SectionEdit[] {
  return withRungs(edits, subsectionId, ladder, (rungs) => [
    ...rungs,
    { item_id: itemId, key, accepted: true },
  ]);
}

/** The ladder without one rung; a dropped suggestion comes back at the next proposal. */
export function dropRung(
  edits: SectionEdit[],
  subsectionId: string,
  ladder: LadderKey,
  itemId: string,
): SectionEdit[] {
  return withRungs(edits, subsectionId, ladder, (rungs) =>
    rungs.filter((rung) => rung.item_id !== itemId),
  );
}

/** The section opened by an empty Definitions subsection the attorney adds, its lines to follow. */
export function addDefinitions(
  edits: SectionEdit[],
  sectionId: string,
  subsectionId: string,
): SectionEdit[] {
  return edits.map((section) =>
    section.section_id === sectionId
      ? {
          ...section,
          subsections: [
            { kind: "definitions", subsection_id: subsectionId, lines: [] },
            ...linesOf(section),
          ],
        }
      : section,
  );
}

/** Whether the framework holds Definitions, placed or waiting in Removed. */
export function holdsDefinitions(
  framework: DetailedDescriptionFramework,
): boolean {
  const placed = (framework.sections ?? []).some((section) =>
    (section.subsections ?? []).some((sub) => sub.kind === "definitions"),
  );
  const removed = (framework.removed ?? []).some((item) =>
    item.kind === "section"
      ? (item.section.subsections ?? []).some((s) => s.kind === "definitions")
      : item.subsection.kind === "definitions",
  );
  return placed || removed;
}

export function addSection(
  edits: SectionEdit[],
  title: string,
  sectionId: string,
): SectionEdit[] {
  return [...edits, { section_id: sectionId, title, subsections: [] }];
}

export function addTextLine(
  edits: SectionEdit[],
  sectionId: string,
  text: string,
  subsectionId: string,
): SectionEdit[] {
  return edits.map((section) =>
    section.section_id === sectionId
      ? {
          ...section,
          subsections: [
            ...linesOf(section),
            { kind: "text", subsection_id: subsectionId, text },
          ],
        }
      : section,
  );
}

export function deleteSubsection(
  edits: SectionEdit[],
  subsectionId: string,
): SectionEdit[] {
  return edits.map((section) => ({
    ...section,
    subsections: linesOf(section).filter(
      (line) => line.subsection_id !== subsectionId,
    ),
  }));
}

/**
 * Delete a section, with its subsections or after moving them to the end of `moveTo`. A
 * `moveTo` that is gone refuses the delete (null), so the lines are never dropped.
 */
export function deleteSection(
  edits: SectionEdit[],
  sectionId: string,
  moveTo?: string,
): SectionEdit[] | null {
  const leaving = edits.find((section) => section.section_id === sectionId);
  const kept = edits.filter((section) => section.section_id !== sectionId);
  if (moveTo === undefined || leaving === undefined) return kept;
  if (!kept.some((section) => section.section_id === moveTo)) return null;
  return kept.map((section) =>
    section.section_id === moveTo
      ? {
          ...section,
          subsections: [...linesOf(section), ...linesOf(leaving)],
        }
      : section,
  );
}

function insertAt<T>(items: T[], position: number, item: T): T[] {
  const at = Math.min(position, items.length);
  return [...items.slice(0, at), item, ...items.slice(at)];
}

/**
 * Put a line back in its section at its place, leaving everything else as it is now: the
 * Undo of one delete. With its section gone there is nowhere to put it (null).
 */
export function putBack(
  edits: SectionEdit[],
  item: RemovedSubsection,
): SectionEdit[] | null {
  const line = asLine(item.subsection);
  if (!edits.some((section) => section.section_id === item.from_section_id)) {
    return null;
  }
  return edits.map((section) =>
    section.section_id === item.from_section_id
      ? {
          ...section,
          subsections: insertAt(linesOf(section), item.position, line),
        }
      : section,
  );
}

/**
 * Put a removed line whose section is gone at the end of the section the attorney chose. The
 * line goes back under its id, which the server reads as a reinstate. With that section gone
 * too, there is nowhere to put it (null).
 */
export function reinstateInto(
  edits: SectionEdit[],
  item: RemovedSubsection,
  sectionId: string,
): SectionEdit[] | null {
  if (!edits.some((section) => section.section_id === sectionId)) return null;
  const line = asLine(item.subsection);
  return edits.map((section) =>
    section.section_id === sectionId
      ? { ...section, subsections: [...linesOf(section), line] }
      : section,
  );
}

/**
 * Put a removed item back where it was. A subsection whose section is gone goes to the end
 * of the first section; with no section to hold it, there is nowhere to put it (null).
 */
export function reinstate(
  edits: SectionEdit[],
  item: RemovedSection | RemovedSubsection,
): SectionEdit[] | null {
  if (item.kind === "section")
    return insertAt(edits, item.position, asSection(item.section));
  const home = putBack(edits, item);
  if (home !== null || edits.length === 0) return home;
  const [first, ...rest] = edits;
  return [
    { ...first, subsections: [...linesOf(first), asLine(item.subsection)] },
    ...rest,
  ];
}

/** The lane the sections themselves are sorted in. */
export const SECTIONS = "sections";

/** The lane a section's subsections are sorted in, named apart from any section id. */
export function laneOf(sectionId: string): string {
  return `lane:${sectionId}`;
}

/** The framework as lists the drag sorts: the sections, then each section's subsections. */
export function frameworkLanes(edits: SectionEdit[]): Record<string, string[]> {
  const lanes: Record<string, string[]> = {
    [SECTIONS]: edits.map((section) => section.section_id),
  };
  for (const section of edits) {
    lanes[laneOf(section.section_id)] = linesOf(section).map(
      (line) => line.subsection_id,
    );
  }
  return lanes;
}

/** Where a drag left `id`: the lane it is in and the rows on either side of it there. */
function dropped(
  lanes: Record<string, string[]>,
  id: string,
): { lane: string; before: string | null; after: string | null } | null {
  for (const [lane, ids] of Object.entries(lanes)) {
    const at = ids.indexOf(id);
    if (at >= 0) {
      return { lane, before: ids[at - 1] ?? null, after: ids[at + 1] ?? null };
    }
  }
  return null;
}

/** Put `item` after the row `before` names, else in front of `after`, else at the end. */
function nextTo<T>(
  items: T[],
  item: T,
  idOf: (row: T) => string,
  { before, after }: { before: string | null; after: string | null },
): T[] {
  const above = items.findIndex((row) => idOf(row) === before);
  if (before === null) return insertAt(items, 0, item);
  if (above >= 0) return insertAt(items, above + 1, item);
  const below = items.findIndex((row) => idOf(row) === after);
  return insertAt(items, below >= 0 ? below : items.length, item);
}

/**
 * The one section or line a drag moved, put beside the rows it was dropped next to in the
 * framework as it is when the edit is sent. So a write that landed after the drag began (a line
 * or section added, an item placed, another window's save) is kept. Null when the item, or the
 * section it was dropped in, is gone.
 */
export function moveAsDragged(
  edits: SectionEdit[],
  lanes: Record<string, string[]>,
  id: string,
): SectionEdit[] | null {
  const where = dropped(lanes, id);
  if (where === null) return null;
  if (where.lane === SECTIONS) {
    const section = edits.find((s) => s.section_id === id);
    if (section === undefined) return null;
    const rest = edits.filter((s) => s !== section);
    return nextTo(rest, section, (s) => s.section_id, where);
  }
  const line = edits.flatMap(linesOf).find((l) => l.subsection_id === id);
  const into = where.lane.slice(laneOf("").length);
  if (line === undefined || !edits.some((s) => s.section_id === into)) {
    return null;
  }
  return deleteSubsection(edits, id).map((section) =>
    section.section_id === into
      ? {
          ...section,
          subsections: nextTo(
            linesOf(section),
            line,
            (l) => l.subsection_id,
            where,
          ),
        }
      : section,
  );
}

/** A figure or an independent claim the framework does not place yet. */
export interface UnplacedItem {
  kind: "figure" | "claim";
  id: string;
}

/** Place an unplaced figure or claim at the end of a section, under a new subsection id. */
export function placeUnplaced(
  edits: SectionEdit[],
  item: UnplacedItem,
  sectionId: string,
  subsectionId: string,
): SectionEdit[] {
  const line: Line =
    item.kind === "figure"
      ? { kind: "figure", subsection_id: subsectionId, file_id: item.id }
      : {
          kind: "claim",
          subsection_id: subsectionId,
          claim_id: item.id,
          text: "",
        };
  return edits.map((section) =>
    section.section_id === sectionId
      ? { ...section, subsections: [...linesOf(section), line] }
      : section,
  );
}
